import React, { useState, useEffect } from "react";
import { Form, Input, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { forgotPassword } from "../../utils/api";

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const onFinish = (values) => {
    setLoading(true);
    setError("");
    forgotPassword({ email: values.email.trim() })
      .then((res) => {
        if (res?.data) {
          setSubmitted(true);
        } else if (res?.response?.data?.message) {
          setError(res.response.data.message);
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Something went wrong. Please try again.");
        setLoading(false);
      });
  };

  return (
    <Spin
      spinning={loading}
      indicator={
        <LoadingOutlined style={{ fontSize: 48, color: "white" }} spin />
      }
    >
      <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

        <div className="relative min-h-screen flex items-center justify-center px-4 py-12 pt-32">
          <div
            className={`w-full max-w-md transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {/* Card */}
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 md:p-10 shadow-2xl">
              {submitted ? (
                /* Success State */
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">Check Your Email</h2>
                  <p className="text-gray-400 mb-8">
                    If an account exists with that email, we've sent a password reset link. Please check your inbox and spam folder.
                  </p>
                  <Link
                    to="/login"
                    className="inline-block py-3 px-8 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg"
                  >
                    Back to Login
                  </Link>
                </div>
              ) : (
                /* Form State */
                <>
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">Forgot Password?</h1>
                    <p className="text-gray-400">Enter your email and we'll send you a reset link</p>
                  </div>

                  <Form
                    name="forgotPassword"
                    onFinish={onFinish}
                    autoComplete="off"
                    layout="vertical"
                    className="space-y-2"
                  >
                    <Form.Item
                      name="email"
                      rules={[
                        { required: true, message: "Please enter your email" },
                        { type: "email", message: "Please enter a valid email" },
                      ]}
                    >
                      <Input
                        placeholder="Email address"
                        className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
                        style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white" }}
                      />
                    </Form.Item>

                    {error && (
                      <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 mb-4">
                        <p className="text-red-300 text-sm text-center">{error}</p>
                      </div>
                    )}

                    <Form.Item className="mb-0">
                      <button
                        type="submit"
                        className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transform hover:-translate-y-0.5"
                      >
                        Send Reset Link
                      </button>
                    </Form.Item>
                  </Form>

                  <p className="text-center text-gray-400 mt-8">
                    Remember your password?{" "}
                    <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors">
                      Sign in
                    </Link>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default ForgotPassword;
